import { useState, type ReactNode } from "react";

import type { ProactiveRule } from "@go-novus/admin-api";
import {
  AlertDialog,
  Button,
  cn,
  EmptyState,
  Icon,
  LinkButton,
  NumberField,
  Panel,
  SegmentedControl,
  SortableList,
  Switch,
  TextArea,
  TextField,
} from "@go-novus/ui";

import * as m from "#/paraglide/messages";

import {
  appendProductTitle,
  canEnableProactiveRule,
  createProactiveRule,
  PRODUCT_TITLE_TOKEN,
  switchProactiveRuleTrigger,
  updateProactiveRule,
  type ProactiveRuleDraft,
} from "#/features/settings";
import { moveItem } from "#/shared/lib/move-item";
import type {
  ElementPickControl,
  PickNotice,
  PickSession,
} from "../../hooks/use-element-pick";
import type { DraftErrors } from "../../lib/settings-draft";

const TRIGGER_META: Record<
  ProactiveRule["trigger"],
  { hint: () => string; label: () => string }
> = {
  element_visible: {
    hint: m.settings_proactive_trigger_element_hint,
    label: m.settings_proactive_trigger_element,
  },
  product_idle: {
    hint: m.settings_proactive_trigger_idle_hint,
    label: m.settings_proactive_trigger_idle,
  },
  store_entry: {
    hint: m.settings_proactive_trigger_entry_hint,
    label: m.settings_proactive_trigger_entry,
  },
};

const TRIGGER_OPTIONS: ProactiveRule["trigger"][] = [
  "product_idle",
  "element_visible",
  "store_entry",
];

const PROMPT_MAX = 200;
const QUESTION_MAX = 1000;

const ERROR_CLASS =
  "m-0 mt-1.5 text-micro [font-weight:var(--font-weight-semibold)] text-[var(--content-vibrant-error-default)]";
const HINT_CLASS =
  "m-0 text-micro [line-height:1.45] text-[var(--content-fg-subtle)]";

/** 事件落在已展开的收起按钮上时，这次操作是收起，不算操作规则。 */
function isCollapseGesture(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest("button[data-rule-toggle][aria-expanded='true']") !== null
  );
}

export interface ProactiveRulesSectionProps {
  errors: DraftErrors;
  onChange: (rules: ProactiveRuleDraft[]) => void;
  /** 某一行被操作时上报它的 id：预览里的主动提示跟着这条规则走。 */
  onOperateRule: (id: string) => void;
  pick: ElementPickControl;
  rules: ProactiveRuleDraft[];
}

/**
 * 主动消息规则：数组顺序就是执行优先级。
 * 列表行报出类型、启停与提示文案；展开后才露出表单。
 * 所有改动只进草稿，Save changes 成功才生效；删除先确认一次。
 */
export function ProactiveRulesSection({
  errors,
  onChange,
  onOperateRule,
  pick,
  rules,
}: ProactiveRulesSectionProps) {
  // 默认收起：列表先给一览，新加的规则和启用不完整规则的行会自动展开。
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  function patchRule(id: string, next: Partial<ProactiveRuleDraft>): void {
    onChange(
      rules.map((rule) =>
        rule.id === id ? updateProactiveRule(rule, next) : rule,
      ),
    );
  }

  function addRule(): void {
    const rule = createProactiveRule(crypto.randomUUID());
    onChange([...rules, rule]);
    setExpandedIds((prev) => new Set(prev).add(rule.id));
    onOperateRule(rule.id);
  }

  /** 启用一条还不完整的规则时顺手展开它，让缺的那项露出来。 */
  function toggleEnabled(id: string, enabled: boolean): void {
    patchRule(id, { enabled });
    if (enabled) {
      setExpandedIds((prev) => new Set(prev).add(id));
    }
  }

  function toggleExpanded(id: string): void {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function confirmDelete(): void {
    if (pendingDeleteId !== null) {
      onChange(rules.filter((rule) => rule.id !== pendingDeleteId));
    }
    setPendingDeleteId(null);
  }

  /**
   * 会话/提示绑定的规则当前有没有行内容器可挂：规则被删、草稿里改了类型、
   * 或者行被收起时行内控件都不在，提示与取消入口要钉到列表顶。
   */
  function pickInlineHidden(ruleId: string | undefined): boolean {
    const rule = rules.find((item) => item.id === ruleId);
    return (
      rule === undefined ||
      rule.trigger !== "element_visible" ||
      !(expandedIds.has(rule.id) || errors.rules?.[rule.id] !== undefined)
    );
  }

  return (
    <Panel>
      <div className="flex items-start justify-between gap-4 px-5 pt-4.75 pb-4">
        <div>
          <h2 className="m-0 mb-1 text-headline font-bold text-[var(--content-fg-strong)]">
            {m.settings_proactive_heading()}
          </h2>
          <p className="m-0 max-w-[62ch] text-label text-[var(--content-fg-subtle)]">
            {m.settings_proactive_copy()}
          </p>
        </div>
        <Button onClick={addRule} size="sm" variant="secondary">
          {m.settings_proactive_add()}
        </Button>
      </div>
      {errors.proactiveRules !== undefined && (
        <p className={cn(ERROR_CLASS, "px-5")} role="alert">
          {errors.proactiveRules}
        </p>
      )}
      {/* 提示没有可挂的元素可见行（规则没了、草稿里改了类型或行被收起）时，
          钉在列表顶上，不落单；没有行可挂按钮，只能关掉。 */}
      {pick.notice !== null && pickInlineHidden(pick.notice.ruleId) && (
        <div className="flex items-center gap-2.5 px-5 pb-4 text-micro">
          <p className={ERROR_CLASS} role="alert">
            {pickNoticeMessage(pick.notice)}
          </p>
          <LinkButton onClick={pick.onDismissNotice}>
            {m.settings_proactive_pick_dismiss()}
          </LinkButton>
        </div>
      )}
      {/* 会话绑定的规则在草稿里被改类型、删除或行被收起时，行内拾取控件
          不在；会话与取消入口钉在列表顶，不落单。 */}
      {pick.session !== null && pickInlineHidden(pick.session.ruleId) && (
        <div className="flex items-center gap-2.5 px-5 pb-4 text-micro [line-height:1.45] text-[var(--content-fg-subtle)]">
          <PickSessionStatus
            onCancel={pick.onCancelPick}
            session={pick.session}
          />
        </div>
      )}
      {rules.length === 0 ? (
        <EmptyState
          body={m.settings_proactive_empty_body()}
          heading={m.settings_proactive_empty_heading()}
          icon={<Icon name="chat" />}
        />
      ) : (
        <SortableList
          items={rules}
          onMove={(id, toIndex) => {
            onChange(moveItem(rules, id, toIndex));
          }}
          renderItem={(rule, { handle, index }) => {
            const position = index + 1;
            const needsTarget = !canEnableProactiveRule(rule);
            const targetHintId = `rule-target-hint-${rule.id}`;
            const ruleErrors = errors.rules?.[rule.id];
            const expanded =
              expandedIds.has(rule.id) || ruleErrors !== undefined;
            const reportOperation = (target: EventTarget | null) => {
              if (!isCollapseGesture(target)) onOperateRule(rule.id);
            };

            return (
              <section
                aria-label={m.settings_proactive_rule({ position })}
                className="border-t border-[var(--border-surface-default)]"
                // 按行认操作：行里任何控件被指针按下或获得焦点都算这条规则；
                // 指针按下单听一道，是因为 Safari 里点按钮不会让按钮获得焦点。
                // 展开/收起按钮是唯一例外：它已经展开时按下或聚焦都是要收起，
                // 不算操作。这样行里以后新加的控件（比如拖动把手）自动算在内。
                onFocusCapture={(event) => {
                  reportOperation(event.target);
                }}
                onPointerDown={(event) => {
                  reportOperation(event.target);
                }}
              >
                <div className="flex items-center gap-3 px-5 py-3.5">
                  {handle}
                  <Switch
                    aria-describedby={needsTarget ? targetHintId : undefined}
                    checked={rule.enabled}
                    disabled={!rule.enabled && needsTarget}
                    label={m.settings_proactive_enable({ position })}
                    onChange={(enabled) => {
                      toggleEnabled(rule.id, enabled);
                    }}
                  />
                  <button
                    aria-expanded={expanded}
                    aria-label={m.settings_proactive_edit({ position })}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 border-0 bg-transparent p-0 text-left font-sans focus-visible:outline-2 outline-[var(--border-accent-default)] focus-visible:outline-offset-2"
                    data-rule-toggle
                    onClick={() => {
                      // 收起按钮被行级监听豁免，展开的上报在这里补：键盘回车
                      // 展开只发 click 也能走到。判断用页面上的展开状态——
                      // 带报错被强制展开的行收不起来，按下不算操作。
                      if (!expanded) onOperateRule(rule.id);
                      toggleExpanded(rule.id);
                    }}
                    type="button"
                  >
                    <span
                      className={cn(
                        "flex-none text-[var(--content-fg-subtle)] [transition:transform_var(--transition-control)]",
                        expanded && "[transform:rotate(90deg)]",
                      )}
                    >
                      <Icon name="chevronRight" size={14} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-title [font-weight:var(--font-weight-semibold)] text-[var(--content-fg-strong)]">
                        {TRIGGER_META[rule.trigger].label()}
                      </span>
                      <span className="block truncate text-label text-[var(--content-fg-subtle)]">
                        {rule.prompt || m.settings_proactive_no_prompt()}
                      </span>
                    </span>
                  </button>
                  <div className="flex flex-none items-center gap-0.5">
                    <Button
                      aria-label={m.settings_proactive_move_up({ position })}
                      disabled={index === 0}
                      icon={<Icon name="chevronUp" size={14} />}
                      onClick={() => {
                        onChange(moveItem(rules, rule.id, index - 1));
                      }}
                      size="sm"
                      variant="quiet"
                    />
                    <Button
                      aria-label={m.settings_proactive_move_down({ position })}
                      disabled={index === rules.length - 1}
                      icon={<Icon name="chevronDown" size={14} />}
                      onClick={() => {
                        onChange(moveItem(rules, rule.id, index + 1));
                      }}
                      size="sm"
                      variant="quiet"
                    />
                    <Button
                      aria-label={m.settings_proactive_delete({ position })}
                      onClick={() => {
                        setPendingDeleteId(rule.id);
                      }}
                      size="sm"
                      variant="quiet"
                    >
                      {m.settings_proactive_delete_confirm()}
                    </Button>
                  </div>
                </div>
                {needsTarget && (
                  <p
                    className={cn(HINT_CLASS, "px-5 pb-3.5")}
                    id={targetHintId}
                  >
                    {m.settings_proactive_target_required()}
                  </p>
                )}
                {expanded && (
                  <div className="grid grid-cols-1 gap-4 px-5 pb-5">
                    <SegmentedControl
                      hint={TRIGGER_META[rule.trigger].hint()}
                      label={m.settings_proactive_trigger_label()}
                      onChange={(value) => {
                        if (
                          value === "product_idle" ||
                          value === "element_visible" ||
                          value === "store_entry"
                        ) {
                          onChange(
                            rules.map((item) =>
                              item.id === rule.id
                                ? switchProactiveRuleTrigger(item, value)
                                : item,
                            ),
                          );
                        }
                      }}
                      options={TRIGGER_OPTIONS.map((value) => ({
                        label: TRIGGER_META[value].label(),
                        value,
                      }))}
                      value={rule.trigger}
                    />
                    {rule.trigger === "element_visible" && (
                      <div>
                        <TextField
                          error={ruleErrors?.selector}
                          hint={m.settings_proactive_selector_hint()}
                          label={m.settings_proactive_selector_label()}
                          onChange={(event) => {
                            patchRule(rule.id, {
                              selector: event.target.value,
                            });
                          }}
                          value={rule.selector}
                        />
                        <PickTargetControl pick={pick} rule={rule} />
                      </div>
                    )}
                    <NumberField
                      error={ruleErrors?.waitSeconds}
                      label={m.settings_proactive_wait_label()}
                      max={300}
                      min={1}
                      onValueChange={(value) => {
                        patchRule(rule.id, { waitSeconds: value });
                      }}
                      suffix={m.settings_proactive_wait_suffix()}
                      value={rule.waitSeconds}
                    />
                    <div>
                      <TextField
                        error={ruleErrors?.prompt}
                        label={m.settings_proactive_prompt_label()}
                        onChange={(event) => {
                          patchRule(rule.id, { prompt: event.target.value });
                        }}
                        value={rule.prompt}
                      />
                      <FieldFooter
                        actions={
                          <LinkButton
                            onClick={() => {
                              patchRule(rule.id, {
                                prompt: appendProductTitle(rule.prompt),
                              });
                            }}
                          >
                            {m.settings_proactive_insert_placeholder({
                              placeholder: PRODUCT_TITLE_TOKEN,
                            })}
                          </LinkButton>
                        }
                        count={rule.prompt.length}
                        max={PROMPT_MAX}
                      >
                        <span>
                          {m.settings_proactive_prompt_hint({
                            placeholder: PRODUCT_TITLE_TOKEN,
                          })}
                        </span>
                      </FieldFooter>
                    </div>
                    <div>
                      <TextArea
                        label={m.settings_proactive_question_label()}
                        onChange={(event) => {
                          patchRule(rule.id, { question: event.target.value });
                        }}
                        value={rule.question}
                      />
                      <FieldFooter
                        count={rule.question.length}
                        max={QUESTION_MAX}
                      >
                        {ruleErrors?.question !== undefined ? (
                          <span
                            className="[font-weight:var(--font-weight-semibold)] text-[var(--content-vibrant-error-default)]"
                            role="alert"
                          >
                            {ruleErrors.question}
                          </span>
                        ) : (
                          <span>
                            {m.settings_proactive_question_hint({
                              placeholder: PRODUCT_TITLE_TOKEN,
                            })}
                          </span>
                        )}
                      </FieldFooter>
                    </div>
                    {ruleErrors?.rule !== undefined && (
                      <p className={ERROR_CLASS} role="alert">
                        {ruleErrors.rule}
                      </p>
                    )}
                  </div>
                )}
              </section>
            );
          }}
        />
      )}
      <AlertDialog
        cancelLabel={m.settings_proactive_delete_cancel()}
        confirmLabel={m.settings_proactive_delete_confirm()}
        description={m.settings_proactive_delete_description()}
        onCancel={() => {
          setPendingDeleteId(null);
        }}
        onConfirm={confirmDelete}
        open={pendingDeleteId !== null}
        title={m.settings_proactive_delete_title()}
        tone="danger"
      />
    </Panel>
  );
}

/**
 * 元素规则的店面拾取入口：签发令牌后新标签打开店面首页，商家自行导航拾取。
 * 入口只对「已保存且草稿干净」的规则放开；App Embed 是否启用我们探测不到
 * （GON-55），指引常驻——没开挂件就不会出现，链接直接拨开关。
 */
interface PickTargetControlProps {
  pick: ElementPickControl;
  rule: ProactiveRuleDraft;
}

function PickTargetControl({ pick, rule }: PickTargetControlProps) {
  const eligibility = pick.eligibility(rule);
  const session =
    pick.session !== null && pick.session.ruleId === rule.id
      ? pick.session
      : null;
  const notice = pick.notice?.ruleId === rule.id ? pick.notice : null;
  const failure = pick.failure?.ruleId === rule.id ? pick.failure : null;

  return (
    <div className="mt-2.5 flex flex-col gap-1.75">
      <div className="flex items-center gap-2.5">
        <Button
          // 会话期间所有入口都关：令牌一次只绑一条规则，别让并发拾取顶掉会话。
          disabled={eligibility !== "ready" || pick.session !== null}
          iconAfter={<Icon name="external" size={13} />}
          onClick={() => {
            pick.onPick(rule.id);
          }}
          size="sm"
          variant="secondary"
        >
          {rule.selector.trim() === ""
            ? m.settings_proactive_pick()
            : m.settings_proactive_pick_again()}
        </Button>
        {session !== null && (
          <span className="text-micro text-[var(--content-fg-subtle)]">
            <PickSessionStatus onCancel={pick.onCancelPick} session={session} />
          </span>
        )}
      </div>
      {eligibility === "save_first" && session === null && (
        <p className={HINT_CLASS}>{m.settings_proactive_pick_save_first()}</p>
      )}
      <p className={HINT_CLASS}>
        {m.settings_proactive_pick_embed_hint()}{" "}
        {pick.themeEditorUrl !== undefined && (
          <a
            className="underline underline-offset-2"
            href={pick.themeEditorUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            {m.settings_theme_editor()}
          </a>
        )}
      </p>
      {failure !== null && (
        <p className={ERROR_CLASS} role="alert">
          {failure.message}
        </p>
      )}
      {notice?.kind === "applied" && (
        <div className="flex items-center gap-2.5 text-micro">
          <p className={HINT_CLASS} role="status">
            {m.settings_proactive_pick_applied()}
          </p>
          <LinkButton onClick={pick.onDismissNotice}>
            {m.settings_proactive_pick_dismiss()}
          </LinkButton>
        </div>
      )}
      {notice?.kind === "gone" && (
        <div className="flex items-center gap-2.5 text-micro">
          <p className={ERROR_CLASS} role="alert">
            {m.settings_proactive_pick_gone()}
          </p>
          <LinkButton onClick={pick.onDismissNotice}>
            {m.settings_proactive_pick_dismiss()}
          </LinkButton>
        </div>
      )}
      {notice?.kind === "conflict" && (
        <div className="flex flex-col gap-1.75">
          <p className={ERROR_CLASS} role="alert">
            {m.settings_proactive_pick_conflict()}
          </p>
          <div className="flex items-center gap-2.5">
            <Button
              onClick={() => {
                pick.onResolveConflict("use");
              }}
              size="sm"
              variant="secondary"
            >
              {m.settings_proactive_pick_use()}
            </Button>
            <Button
              onClick={() => {
                pick.onResolveConflict("keep");
              }}
              size="sm"
              variant="quiet"
            >
              {m.settings_proactive_pick_keep()}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** 拾取会话的状态行：进行中文案（或收尾失败提示）加可点的取消，两处共用。 */
function PickSessionStatus({
  onCancel,
  session,
}: {
  onCancel: () => void;
  session: PickSession;
}) {
  return (
    <>
      {session.phase === "cancel_failed" ? (
        <span
          className="[font-weight:var(--font-weight-semibold)] text-[var(--content-vibrant-error-default)]"
          role="alert"
        >
          {m.settings_proactive_pick_cancel_failed()}
        </span>
      ) : (
        <span role="status">{m.settings_proactive_picking()}</span>
      )}{" "}
      {session.phase !== "canceling" && (
        <LinkButton onClick={onCancel}>
          {m.settings_proactive_pick_cancel()}
        </LinkButton>
      )}
    </>
  );
}

/** 置顶提示的文案按提示种类取：冲突与失效是问题，applied 只是知会。 */
function pickNoticeMessage(notice: PickNotice): string {
  switch (notice.kind) {
    case "applied":
      return m.settings_proactive_pick_applied();
    case "conflict":
      return m.settings_proactive_pick_conflict();
    case "gone":
      return m.settings_proactive_pick_gone();
  }
}

/** 字段底栏：左侧 hint/错误，右侧可选动作加字数。 */
function FieldFooter({
  actions,
  children,
  count,
  max,
}: {
  actions?: ReactNode;
  children: ReactNode;
  count: number;
  max: number;
}) {
  return (
    <div className="mt-1.25 flex items-center justify-between gap-3 text-micro text-[var(--content-fg-subtle)]">
      {children}
      <span className="flex flex-none items-center gap-3">
        {actions}
        <span>{m.settings_proactive_field_count({ count, max })}</span>
      </span>
    </div>
  );
}
